// NFT media helpers — resilient IPFS gateway resolution + fallback.
//
// Background: NFT metadata `image`/`animation_url` values point at the dedicated
// `magiccraft.mypinata.cloud` gateway. That gateway started returning 403, which
// broke every NFT image/video across the app. The underlying media is IPFS-pinned,
// so it is retrievable from any public gateway — we just need to normalise the URL
// to its CID and try a chain of gateways until one succeeds.

// Legacy IPFS hash remap (was duplicated inline as `getThumbnailImage` in
// NFTMarketplace.js and NFTItem.js — consolidated here as the single source).
const HASH_REMAP = {
  QmTPtJSxd3YWXNKfYLYkVVUe4nazk3yxhp2XQfgARzcs7y:
    'QmVHWN3nP2vpMjT2w1s3fRRvHPEGtGHXoTQbkYEXZHsZBq',
  QmX1dhoSQqq9eMyzzFBSAYgQ5119Ap8h7VC2evppGs93W9:
    'QmbYXYGUYbWPALxh66MUbrUH7FC7eUqa8rXeVMQMDbGrga',
  QmNdDumUU4KDSScPkohP4fH2T5YwxNJTbQf4Cy8D3TcP59:
    'QmX8UUL4vE3NzRaXMivCoyL9TR6PR2A9GpvcVw9UdXB3Yb',
};

// Ordered gateway list. The primary can be overridden via REACT_APP_IPFS_GATEWAY
// (e.g. to restore magiccraft.mypinata.cloud once a gateway access token is set).
// Public fallbacks keep media working regardless.
const PRIMARY_GATEWAY =
  process.env.REACT_APP_IPFS_GATEWAY || 'https://gateway.pinata.cloud/ipfs/';

export const IPFS_GATEWAYS = [
  PRIMARY_GATEWAY,
  'https://gateway.pinata.cloud/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://dweb.link/ipfs/',
]
  .map((g) => (g.endsWith('/') ? g : `${g}/`))
  .filter((g, i, a) => a.indexOf(g) === i);

export const gatewayCount = () => IPFS_GATEWAYS.length;

// Apply the legacy old→new hash remap.
export const remapHash = (url = '') => {
  if (!url) return url;
  let out = url;
  Object.keys(HASH_REMAP).forEach((oldHash) => {
    if (out.includes(oldHash)) out = out.replace(oldHash, HASH_REMAP[oldHash]);
  });
  return out;
};

// Pull "<cid>/<optional path>" out of any ipfs:// or gateway URL. Returns null
// for non-IPFS URLs (regular https assets, data URIs, imported bundler assets).
export const extractIpfsPath = (url = '') => {
  if (!url || typeof url !== 'string') return null;
  const u = remapHash(url);
  // ipfs://<cid>/...  (also ipfs://ipfs/<cid>)
  let m = u.match(/^ipfs:\/\/(?:ipfs\/)?(.+)$/i);
  if (m) return m[1];
  // https://<host>/ipfs/<cid>/...
  m = u.match(/\/ipfs\/(.+)$/i);
  if (m) return m[1];
  // https://<cid>.ipfs.<gateway>/<path>
  m = u.match(/^https?:\/\/([a-z0-9]+)\.ipfs\.[^/]+\/?(.*)$/i);
  if (m) return m[2] ? `${m[1]}/${m[2]}` : m[1];
  return null;
};

// Resolve an NFT media URL against the gateway at `index`, falling through the
// list. Non-IPFS URLs are returned as-is (after hash remap).
export const resolveNftMedia = (url = '', index = 0) => {
  if (!url || typeof url !== 'string') return '';
  const remapped = remapHash(url);
  const path = extractIpfsPath(remapped);
  if (!path) return remapped;
  const gateway = IPFS_GATEWAYS[Math.min(index, IPFS_GATEWAYS.length - 1)];
  const clean = path.split('?')[0].replace(/^\/+/, '');
  return `${gateway}${clean}`;
};

export const isVideoUrl = (url = '') =>
  /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url) || /(^|[/_-])mp4/i.test(url);

// Inline SVG placeholder shown when every gateway fails (no asset dependency).
export const NFT_MEDIA_FALLBACK =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240">
      <rect width="240" height="240" fill="#182252"/>
      <rect x="0.5" y="0.5" width="239" height="239" fill="none" stroke="#2e3458"/>
      <g fill="none" stroke="#5b86ff" stroke-width="3" opacity="0.6">
        <circle cx="120" cy="104" r="30"/>
        <path d="M78 168l26-30 18 18 20-24 20 36z" stroke-linejoin="round"/>
      </g>
      <text x="120" y="206" fill="#8b93c4" font-family="Roboto,Arial,sans-serif" font-size="15" text-anchor="middle">NFT unavailable</text>
    </svg>`,
  );
